import {
    featureFlagsApi,
    type ListFeatureFlagsRequest,
} from '@/api/featureFlags';
import type { FeatureFlag } from '@/types/models';
import { useGet } from '@foodics/ui-common';
import { createGlobalState } from '@vueuse/core';
import { useCurrentUser } from './currentUser';
import { watch } from 'vue';

export const useFeatureFlags = createGlobalState(() => {
    const { user } = useCurrentUser();

    const { data, isLoading, error, execute } = useGet<
        ListFeatureFlagsRequest['response']
    >(featureFlagsApi.list, { disableAutoExecute: true });

    watch(
        user,
        () => {
            if (!user.value) return;

            execute();
        },
        { immediate: true },
    );

    const hasFeatureFlags = (
        featureFlag: FeatureFlag | FeatureFlag[],
        match: 'all' | 'any' = 'all',
    ) => {
        const featureFlagList = Array.isArray(featureFlag)
            ? featureFlag
            : [featureFlag];

        // if no feature flags are provided, return true
        if (featureFlagList.length === 0) return true;

        // if the feature flags are not loaded, return false
        if (!data.value) return false;
        const userFeatureFlags = data.value.data.features;

        return match === 'all'
            ? featureFlagList.every((flag) => userFeatureFlags[flag])
            : featureFlagList.some((flag) => userFeatureFlags[flag]);
    };

    return {
        data,
        isLoading,
        error,
        hasFeatureFlags,
    };
});
